import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Heart, Dog, Cat } from "lucide-react";
import StatsBox from "../../../Components/StatsBox";
import { apiClient } from "../../../lib/apiClient";

interface KennelStats {
  success: boolean;
  stats: {
    kennels: {
      total: number;
      withAnimals: number;
      empty: number;
    };
    animals: {
      total: number;
      dogs: {
        total: number;
        available: number;
        adopted: number;
      };
      cats: {
        total: number;
        available: number;
        adopted: number;
      };
    };
  };
}

const Counter = () => {
  const { t } = useTranslation("landing");
  const [adoptedDogs, setAdoptedDogs] = useState<number>(0);
  const [adoptedCats, setAdoptedCats] = useState<number>(0);
  // targetAvailable: final value from API; animatedAvailable: value shown during animation
  const [targetAvailable, setTargetAvailable] = useState<number>(0);
  const [animatedAvailable, setAnimatedAvailable] = useState<number>(0);
  const [targetDogs, setTargetDogs] = useState<number>(0);
  const [targetCats, setTargetCats] = useState<number>(0);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const countRef = useRef<HTMLDivElement>(null);

  // Fetch adopted animals count from API
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await apiClient.get<KennelStats>("/api/kennels/stats");
        if (response.success && response.stats) {
          setTargetDogs(response.stats.animals.dogs.adopted);
          setTargetCats(response.stats.animals.cats.adopted);
          setTargetAvailable(
            response.stats.animals.dogs.available +
              response.stats.animals.cats.available,
          );
        }
      } catch (error) {
        console.error("Failed to fetch adoption stats:", error);
        setTargetDogs(0);
        setTargetCats(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Animate counts when visible
  useEffect(() => {
    if (isLoading) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);

          const steps = 50;
          const dogIncrement = targetDogs / steps;
          const catIncrement = targetCats / steps;
          const availableIncrement = targetAvailable / steps;
          let currentDogs = 0;
          let currentCats = 0;
          let currentAvailable = 0;
          let step = 0;

          const timerId = window.setInterval(() => {
            step++;
            currentDogs += dogIncrement;
            currentCats += catIncrement;
            currentAvailable += availableIncrement;

            if (step >= steps) {
              setAdoptedDogs(targetDogs);
              setAdoptedCats(targetCats);
              setAnimatedAvailable(targetAvailable);
              window.clearInterval(timerId);
            } else {
              setAdoptedDogs(Math.floor(currentDogs));
              setAdoptedCats(Math.floor(currentCats));
              setAnimatedAvailable(Math.floor(currentAvailable));
            }
          }, 20);
        }
      },
      { threshold: 0.5 },
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    let cleanupCalled = false;
    return () => {
      if (cleanupCalled) return;
      cleanupCalled = true;
      if (countRef.current) {
        observer.unobserve(countRef.current);
      }
    };
  }, [isVisible, isLoading, targetDogs, targetCats]);

  return (
    <div ref={countRef}>
      <StatsBox
        icon={<Heart className="w-16 h-16" />}
        counts={[
          {
            value: adoptedDogs + adoptedCats,
            label: t("counter.adopted"),
            icon: <Heart className="w-5 h-5" />,
          },
          {
            value: animatedAvailable,
            label: t("counter.available"),
            icon: <Dog className="w-5 h-5" />,
          },
        ]}
        title={t("counter.title")}
        subtitle={t("counter.subtitle")}
        isLoading={isLoading}
      />
    </div>
  );
};

export default Counter;
